import React, { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import {
	SypacButton,
	SypacCheckbox,
	SypacInput,
	SypacText,
} from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import Flag from 'react-world-flags';
import * as Yup from 'yup';
import { CompanyDetailsProps, CompanyProps } from './CompanyDetails.interface';
import { COUNTRIES } from '../../../DropDownOption/countries.constant';
import { DropDownOption } from '../../../DropDownOption/DropDownOption';
import {
	colourErrorStyles,
	colourStyles,
} from '../../../DropDownOption/DropDownStyles';
import { WhatsUp } from '../../../../assets/WhatsUp';
import { Telegram } from '../../../../assets/Telegram';
import { Viber } from '../../../../assets/Viber';
import CustomOption, {
	customStylesCompanyDetails,
	optionsCompanyDetails,
} from './CustomOption/CustomOption';
import { useGetCompanyById } from '../../../../hooks/use-get-company-by-id';
import { CompanyService } from '../../../../services/company.services';
import { toastVariant } from '../../../ToastVariant/toastVariant';
import LoadingSpinner from '../../../../assets/LoadingSpinner';
import { Geo } from '../../../../interfaces/geo.interface';
import { useDetectClickOutside } from 'react-detect-click-outside';
import useDebounce from '../../../../hooks/useDebounce';
import { GeoService } from '../../../../services/geo.services';
import { CompanyStateProps } from '../../../CompanyInformationEditable/CompanyInformationEditable.interface';
import { DatePicker } from '../../../DatePicker/DatePicker';
import { formatAddress } from '../../../../utils/string.util';

const CompanyDetails = ({
	companyId,
	onSubmit,
	onBack,
}: CompanyDetailsProps) => {
	const { t } = useTranslate();
	const language = localStorage.getItem('lang') || 'en';
	const [billingAddressCheckbox, setBillingAddressCheckbox] =
		useState<boolean>(false);
	const [selectedCountry, selectCountry] = useState<CompanyStateProps>();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [company] = useGetCompanyById({
		id: companyId,
	});

	const [locationResults, setLocationResults] = useState<boolean>(false);
	const [searchLocation, setSearchLocation] = useState<string>('');
	const searchQuery = useDebounce(searchLocation, 500);
	const [locations, setLocations] = useState<Geo[]>([]);

	const [billingLocationResults, setBillingLocationResults] =
		useState<boolean>(false);
	const [searchBillingLocation, setSearchBillingLocation] =
		useState<string>('');
	const billingSearchQuery = useDebounce(searchBillingLocation, 500);
	const [billingLocations, setBillingLocations] = useState<Geo[]>([]);

	const getSearchLocation = useCallback(() => {
		if (searchQuery.length) {
			GeoService.getLocations({ phrase: searchQuery, maxResults: '10' })
				.then(({ data }) => {
					setLocations(data);
				})
				.catch(console.error);
		} else {
			setLocations([]);
		}
	}, [searchQuery]);

	useEffect(() => {
		getSearchLocation();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery]);

	const getBillingSearchLocation = useCallback(() => {
		if (billingSearchQuery.length) {
			GeoService.getLocations({ phrase: billingSearchQuery, maxResults: '10' })
				.then(({ data }) => {
					setBillingLocations(data);
				})
				.catch(console.error);
		} else {
			setBillingLocations([]);
		}
	}, [billingSearchQuery]);

	useEffect(() => {
		getBillingSearchLocation();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [billingSearchQuery]);

	useEffect(() => {
		if (company?.countryCode) {
			const country = COUNTRIES.find(
				(item) => item.value === company.countryCode,
			);
			selectCountry({ value: country?.value!, label: country?.label! });
		}
	}, [company?.countryCode]);

	const setBillingAddressSame = () => {
		const isSame = !billingAddressCheckbox;
		setBillingAddressCheckbox(isSame);

		if (isSame) {
			formik.setFieldValue('billingAddress', formik.values.registryAddress);
		} else {
			formik.setFieldValue('billingAddress', '');
		}
	};

	const setCountry = (data: any, _: any) => {
		selectCountry(data);
		formik.setFieldValue('country', data.label);
	};

	function onChange(option: any) {
		formik.setFieldValue(
			'companyTypes',
			option ? option?.map((item: any) => item.value) : [],
		);
	}

	const getValue = () => {
		if (optionsCompanyDetails && formik.values.companyTypes?.length) {
			return optionsCompanyDetails.filter(
				(option) => formik.values.companyTypes!.indexOf(option.value) >= 0,
			);
		}
	};

	const formik = useFormik({
		initialValues: {
			name: company?.name,
			establishedDate: company?.establishedDate,
			companyTypes: company?.companyTypes || [],
			countryCode: company?.countryCode,
			vatId: company?.vatId,
			registrationNumber: company?.registrationNumber?.toString(),
			taxNumber: company?.taxNumber?.toString(),
			registryAddress: company?.registryAddress?.replace('-', ''),
			billingAddress: company?.billingAddress?.replace('-', ''),
			email: company?.contact?.email,
			secondaryEmail: company?.contact?.secondaryEmail,
			phoneNumberPrimary: company?.contact?.phone?.phoneNumber,
			phoneNumberSecondary: company?.contact?.secondaryPhone?.phoneNumber,
			primaryIsViber: company?.contact?.phone?.isViber,
			primaryIsTelegram: company?.contact?.phone?.isTelegram,
			primaryIsWhatsUp: company?.contact?.phone?.isWhatsapp,
			secondaryIsViber: company?.contact?.secondaryPhone?.isViber,
			secondaryIsTelegram: company?.contact?.secondaryPhone?.isTelegram,
			secondaryIsWhatsUp: company?.contact?.secondaryPhone?.isWhatsapp,
		},
		enableReinitialize: true,
		validationSchema: Yup.object({
			billingAddress: Yup.string().required(
				t(
					'companyInformationEditable.billingAddress',
					'Billing Address is required',
				),
			),
			registryAddress: Yup.string().required(
				t(
					'companyInformationEditable.registryAddress',
					'Registration address is required',
				),
			),
			taxNumber: Yup.string().required(
				t(
					'companyInformationEditable.taxNumber',
					'Tax Identification Number is required',
				),
			),
		}),
		onSubmit: async (values) => {
			setIsLoading(true);
			try {
				const companyData: CompanyProps = {
					name: values.name,
					establishedDate: values.establishedDate,
					companyTypes: values.companyTypes,
					countryCode: values.countryCode,
					vatId: values.vatId,
					registrationNumber: values?.registrationNumber?.toString(),
					taxNumber: values?.taxNumber,
					registryAddress: values.registryAddress,
					billingAddress: values.billingAddress,
					contact: {
						email: values.email,
						secondaryEmail: values.secondaryEmail,
						phone: {
							phoneNumber: values.phoneNumberPrimary,
							isTelegram: values.primaryIsTelegram,
							isViber: values.primaryIsViber,
							isWhatsapp: values.primaryIsWhatsUp,
						},
						secondaryPhone: {
							phoneNumber: values.phoneNumberSecondary,
							isTelegram: values.secondaryIsTelegram,
							isViber: values.secondaryIsViber,
							isWhatsapp: values.secondaryIsWhatsUp,
						},
					},
				};

				await CompanyService.companyVerificationStep1(companyId, {
					...companyData,
				});
				onSubmit();
			} catch (e) {
			} finally {
				setIsLoading(false);
			}
		},
	});

	useEffect(() => {
		if (billingAddressCheckbox) {
			formik.setFieldValue('billingAddress', formik.values.registryAddress);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [billingAddressCheckbox, formik.values.registryAddress]);

	const onSelectLocation = (location: Geo) => {
		formik.setFieldValue('registryAddress', location.label);
		setLocationResults(false);
	};

	const onSelectBillingLocation = (location: Geo) => {
		formik.setFieldValue('billingAddress', location.label);
		setBillingLocationResults(false);
	};

	const onFormSubmit = (e: React.FormEvent<HTMLFormElement> | undefined) => {
		formik.validateForm(formik.values).then(() => {});
		formik.handleSubmit(e);
		if (!formik.isValid) {
			toastVariant(
				t(
					'companyInformationEditable.pleaseFillRequiredFields',
					'Please fill in all required fields',
				),
				true,
			);
		}
	};

	const registryRef = useDetectClickOutside({
		onTriggered: () => setLocationResults(false),
	});
	const billingRef = useDetectClickOutside({
		onTriggered: () => setBillingLocationResults(false),
	});

	return (
		<form onSubmit={onFormSubmit} className="flex flex-col gap-6">
			<div className="flex flex-col px-10 py-5 gap-6">
				<div className="flex justify-between items-center pb-5">
					<SypacText variant="body-regular-medium">
						<p className="text-gray-90 -tracking-[0.01em]">
							<T keyName="companyDetails.companyDetails">Company details</T>
						</p>
					</SypacText>
				</div>
				<div className="grid grid-cols-2 gap-4 company-information-editable-border-bottom pb-5">
					<SypacInput error={!!(formik.touched.name && formik.errors.name)}>
						<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
							<T keyName="companyDetails.companyName">Company Name</T>
						</label>
						<input
							className="py-2.5 pl-3 border rounded-md placeholder:text-gray-22"
							name="name"
							type="text"
							placeholder={t(
								'companyDetails.enterCompanyName',
								'Enter Company Name',
							)}
							value={formik.values.name}
							onChange={(event) => formik?.handleChange(event)}
						/>
					</SypacInput>
					<SypacInput
						error={
							!!(
								formik.touched.establishedDate && formik.errors.establishedDate
							)
						}
					>
						<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
							<T keyName="companyDetails.established">Established</T>
						</label>
						<DatePicker
							min="1950-01-01"
							max={dayjs().toString()}
							value={formik?.values.establishedDate?.toString()}
							onChange={(date) => formik.setFieldValue('establishedDate', date)}
							placeholder={t('companyDetails.enterDate', 'Enter date')}
							allowOtherMonthSelection={true}
						/>
					</SypacInput>
					<SypacInput
						error={
							!!(formik.touched.companyTypes && formik.errors.companyTypes)
						}
					>
						<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
							<T keyName="companyDetails.companyType">Company type</T>
						</label>
						<Select
							placeholder={t(
								'companyDetails.selectCompanyType',
								'Select Company Type',
							)}
							isMulti
							name="companyTypes"
							isClearable={false}
							styles={customStylesCompanyDetails}
							options={optionsCompanyDetails}
							value={getValue()}
							onChange={onChange}
							components={{ Option: CustomOption }}
						/>
					</SypacInput>
					<SypacInput
						error={!!(formik.touched.countryCode && formik.errors.countryCode)}
					>
						<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
							<T keyName="companyDetails.country">Country</T>
						</label>
						<Select
							placeholder={t('companyDetails.country', 'Country')}
							name="countryCode"
							onChange={setCountry}
							onBlur={formik.handleBlur}
							options={COUNTRIES}
							menuPlacement={'auto'}
							components={{
								Option: (options) =>
									DropDownOption(options, formik.values.countryCode),
							}}
							classNamePrefix="dropdown"
							value={
								formik.values.countryCode
									? {
											value: selectedCountry?.value,
											label: (
												<div className="flex">
													<div className="w-full min-w-full h-8 flex justify-start items-center hover:bg-mercury-50">
														<Flag
															code={selectedCountry?.value}
															className="rounded"
															width={40}
															height={24}
														/>
														<div className="ml-2 text-base text-gray-100 truncate">
															{selectedCountry?.label}
														</div>
													</div>
												</div>
											),
									  }
									: ''
							}
							styles={
								formik.touched.countryCode && formik.errors.countryCode
									? { ...colourStyles, ...colourErrorStyles }
									: colourStyles
							}
						/>
					</SypacInput>
					<SypacInput error={!!(formik.touched.vatId && formik.errors.vatId)}>
						<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
							<T keyName="companyDetails.vatNumber">VAT Number</T>
						</label>
						<input
							className="py-2.5 pl-3 border rounded-md placeholder:text-gray-22"
							name="vatId"
							type="text"
							placeholder={t(
								'companyDetails.enterCompanyType',
								'Enter Company Type',
							)}
							value={formik.values.vatId}
							onChange={(event) => formik?.handleChange(event)}
						/>
					</SypacInput>
					<SypacInput
						error={!!(formik.touched.taxNumber && formik.errors.taxNumber)}
					>
						<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
							<T keyName="companyDetails.companyTaxNumber">
								Tax Identification Number
							</T>
						</label>
						<input
							className="py-2.5 pl-3 border rounded-md placeholder:text-gray-22"
							name="taxNumber"
							type="text"
							placeholder={t(
								'companyDetails.enterCompanyTaxNumber',
								'Enter Tax Identification Number',
							)}
							value={formik.values.taxNumber}
							onChange={(event) => formik?.handleChange(event)}
						/>
						{formik?.touched.taxNumber && formik?.errors.taxNumber ? (
							<span className="input-error">{formik.errors.taxNumber}</span>
						) : null}
					</SypacInput>
					<SypacInput
						error={
							!!(
								formik.touched.registrationNumber &&
								formik.errors.registrationNumber
							)
						}
					>
						<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
							<T keyName="companyDetails.companyRegistryNumber">
								Company Registration Number
							</T>
						</label>
						<input
							className="py-2.5 pl-3 border rounded-md placeholder:text-gray-22"
							name="registrationNumber"
							type="text"
							placeholder={t(
								'companyDetails.enterCompanyRegistryNumber',
								'Enter Company Registration Number',
							)}
							value={formik.values.registrationNumber}
							onChange={(event) => formik?.handleChange(event)}
						/>
					</SypacInput>
				</div>
				<div className="flex flex-col gap-6">
					<SypacText variant="body-regular-medium">
						<p className="text-gray-90 -tracking-[0.01em]">
							<T keyName="companyDetails.contactInfo">Contact Info</T>
						</p>
					</SypacText>
					<div className="grid grid-cols-2 gap-x-4 gap-y-5">
						<div className="flex flex-col gap-2">
							<div ref={registryRef} className="relative">
								<SypacInput
									error={
										!!(
											formik.touched.registryAddress &&
											formik.errors.registryAddress
										)
									}
								>
									<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
										<T keyName="companyDetails.registryAddress">
											Registration address
										</T>
									</label>

									<input
										autoComplete="off"
										type="text"
										name="registryAddress"
										className={`py-2.5 pl-3 border rounded-md placeholder:text-gray-22 transition ${
											locationResults
												? 'border-cornflower-blue'
												: 'hover:border-gray-30'
										}`}
										placeholder={t(
											'companyDetails.enterRegistryAddress',
											'Enter Registration address',
										)}
										onBlur={formik.handleBlur}
										value={formik?.values.registryAddress}
										onFocus={() => setLocationResults(true)}
										onChange={(event) => {
											formik.handleChange(event);
											setSearchLocation(event.target.value);
										}}
									/>
									{formik?.touched.registryAddress &&
									formik?.errors.registryAddress ? (
										<span className="input-error">
											{formik.errors.registryAddress}
										</span>
									) : null}
								</SypacInput>

								<div
									className={`absolute top-[70px] w-[303px] flex-col shadow-dropdown border-[1px] border-gray-10 rounded-lg max-h-72 overflow-hidden bg-white z-50 ${
										locationResults ? 'flex' : 'hidden'
									}`}
								>
									{locations?.length ? (
										<div className="flex flex-col w-full p-3 overflow-y-scroll">
											{locations.map((location: Geo) => (
												<div
													key={location.locationId}
													className="flex items-center hover:bg-gray-10-opacity-50 rounded-md cursor-pointer mr-[9px]"
													onClick={() => onSelectLocation(location)}
												>
													<div className="mx-5">
														<Flag
															className="rounded-[3px]"
															code={location.countryCode.toUpperCase()}
															width={22}
															height={16}
														/>
													</div>
													<SypacText
														variant="body-normal-medium"
														className="my-[15px]"
													>
														<p>{formatAddress(location.address)}</p>
													</SypacText>
												</div>
											))}
										</div>
									) : null}
								</div>
							</div>

							<div className="flex flex-row items-center">
								<SypacCheckbox size="md">
									<input
										name="billingAddressCheckbox"
										checked={billingAddressCheckbox}
										onChange={setBillingAddressSame}
										type="checkbox"
										className="cursor-pointer"
									/>
								</SypacCheckbox>
								<SypacText variant="overline-normal-large">
									<p className="text-gray-90 pl-1">
										<T keyName="companyDetails.billingAddressSame">
											Billing address the same
										</T>
									</p>
								</SypacText>
							</div>
						</div>

						<div ref={billingRef} className="relative">
							<SypacInput
								error={
									!!(
										formik.touched.billingAddress &&
										formik.errors.billingAddress
									)
								}
								disabled={billingAddressCheckbox}
							>
								<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
									<T keyName="companyDetails.billingAddress">Billing address</T>
								</label>

								<input
									autoComplete="off"
									type="text"
									name="billingAddress"
									className={`py-2.5 pl-3 border rounded-md placeholder:text-gray-22 transition ${
										billingLocationResults
											? 'border-cornflower-blue'
											: 'hover:border-gray-30'
									}`}
									placeholder={t(
										'companyDetails.enterBillingAddress',
										'Enter Billing address',
									)}
									onBlur={formik.handleBlur}
									value={formik?.values.billingAddress}
									onFocus={() => setBillingLocationResults(true)}
									onChange={(event) => {
										formik.handleChange(event);
										setSearchBillingLocation(event.target.value);
									}}
								/>
								{formik?.touched.billingAddress &&
								formik?.errors.billingAddress ? (
									<span className="input-error">
										{formik.errors.billingAddress}
									</span>
								) : null}
							</SypacInput>

							<div
								className={`absolute top-[70px] w-[303px] flex-col shadow-dropdown border-[1px] border-gray-10 rounded-lg max-h-72 overflow-hidden bg-white z-50 ${
									billingLocationResults ? 'flex' : 'hidden'
								}`}
							>
								{billingLocations?.length ? (
									<div className="flex flex-col w-full p-3 overflow-y-scroll">
										{billingLocations.map((location: Geo) => (
											<div
												key={location.locationId}
												className="flex items-center hover:bg-gray-10-opacity-50 rounded-md cursor-pointer mr-[9px]"
												onClick={() => onSelectBillingLocation(location)}
											>
												<div className="mx-5">
													<Flag
														className="rounded-[3px]"
														code={location.countryCode.toUpperCase()}
														width={22}
														height={16}
													/>
												</div>
												<SypacText
													variant="body-normal-medium"
													className="my-[15px]"
												>
													<p>{formatAddress(location.address)}</p>
												</SypacText>
											</div>
										))}
									</div>
								) : null}
							</div>
						</div>

						<SypacInput error={!!(formik.touched.email && formik.errors.email)}>
							<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
								<T keyName="companyDetails.emailAddress">Email address</T>
							</label>
							<input
								className="py-2.5 pl-3 border rounded-md placeholder:text-gray-22"
								name="email"
								type="text"
								placeholder={t(
									'companyDetails.enterEmailAddress',
									'Enter Email address',
								)}
								value={formik.values.email}
								onChange={(event) => formik?.handleChange(event)}
							/>
						</SypacInput>
						<SypacInput
							error={
								!!(
									formik.touched.secondaryEmail && formik.errors.secondaryEmail
								)
							}
						>
							<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
								<T keyName="companyDetails.secondaryEmailAddress">
									Secondary email address
								</T>
							</label>
							<input
								className="py-2.5 pl-3 border rounded-md placeholder:text-gray-22"
								name="secondaryEmail"
								type="text"
								placeholder={t(
									'companyDetails.enterSecondaryEmailAddress',
									'Enter Secondary email address',
								)}
								value={formik.values.secondaryEmail}
								onChange={(event) => formik?.handleChange(event)}
							/>
						</SypacInput>
						<div className="flex flex-col gap-4">
							<SypacInput
								error={
									!!(
										formik.touched.phoneNumberPrimary &&
										formik.errors.phoneNumberPrimary
									)
								}
							>
								<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
									<T keyName="companyDetails.primaryPhoneNumber">
										Primary phone number
									</T>
								</label>
								<input
									className="py-2.5 pl-3 border rounded-md placeholder:text-gray-22"
									name="phoneNumberPrimary"
									type="text"
									placeholder={t(
										'companyDetails.enterPrimaryPhoneNumber',
										'Enter Primary Phone Number',
									)}
									value={formik.values.phoneNumberPrimary}
									onChange={(event) => formik?.handleChange(event)}
								/>
							</SypacInput>
							<div className="flex justify-end gap-[14px]">
								<div className="flex gap-1.5">
									<SypacCheckbox size="md">
										<input
											name="primaryIsWhatsUp"
											checked={formik.values.primaryIsWhatsUp}
											onChange={(event) => formik?.handleChange(event)}
											type="checkbox"
										/>
									</SypacCheckbox>
									<WhatsUp />
								</div>
								<div className="flex gap-1.5">
									<SypacCheckbox size="md">
										<input
											name="primaryIsTelegram"
											checked={formik.values.primaryIsTelegram}
											onChange={(event) => formik?.handleChange(event)}
											type="checkbox"
										/>
									</SypacCheckbox>
									<Telegram />
								</div>
								<div className="flex gap-1.5">
									<SypacCheckbox size="md">
										<input
											name="primaryIsViber"
											checked={formik.values.primaryIsViber}
											onChange={(event) => formik?.handleChange(event)}
											type="checkbox"
										/>
									</SypacCheckbox>
									<Viber />
								</div>
							</div>
						</div>
						<div className="flex flex-col gap-4">
							<SypacInput
								error={
									!!(
										formik.touched.phoneNumberSecondary &&
										formik.errors.phoneNumberSecondary
									)
								}
							>
								<label className="text-xs leading-4 text-primary-dark-gray -tracking-[0.01em]">
									<T keyName="companyDetails.secondaryPhoneNumber">
										Secondary phone number
									</T>
								</label>
								<input
									className="py-2.5 pl-3 border rounded-md placeholder:text-gray-22"
									name="phoneNumberSecondary"
									type="text"
									placeholder={t(
										'companyDetails.enterSecondaryPhoneNumber',
										'Enter Secondary Phone Number',
									)}
									value={formik.values.phoneNumberSecondary}
									onChange={(event) => formik?.handleChange(event)}
								/>
							</SypacInput>
							<div className="flex justify-end gap-[14px]">
								<div className="flex gap-1.5">
									<SypacCheckbox size="md">
										<input
											name="secondaryIsWhatsUp"
											checked={formik.values.secondaryIsWhatsUp}
											onChange={(event) => formik?.handleChange(event)}
											type="checkbox"
										/>
									</SypacCheckbox>
									<WhatsUp />
								</div>
								<div className="flex gap-1.5">
									<SypacCheckbox size="md">
										<input
											name="secondaryIsTelegram"
											checked={formik.values.secondaryIsTelegram}
											onChange={(event) => formik?.handleChange(event)}
											type="checkbox"
										/>
									</SypacCheckbox>
									<Telegram />
								</div>
								<div className="flex gap-1.5">
									<SypacCheckbox size="md">
										<input
											name="secondaryIsViber"
											checked={formik.values.secondaryIsViber}
											onChange={(event) => formik?.handleChange(event)}
											type="checkbox"
										/>
									</SypacCheckbox>
									<Viber />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="flex justify-center w-full py-5 gap-6 border-0 border-t border-solid border-gray-10">
				<SypacButton variant="secondary" size="small">
					<button className="px-6 py-3" onClick={onBack}>
						<SypacText variant="body-regular-medium">
							<p>
								<T keyName="companyDetails.back">Back</T>
							</p>
						</SypacText>
					</button>
				</SypacButton>
				<SypacButton variant="primary" size="small">
					<button className="px-6 py-3" type="submit">
						<SypacText
							variant="body-regular-medium"
							className="flex items-center gap-4"
						>
							<p>
								<T keyName="companyDetails.nextStep">Next step</T>
							</p>
							{isLoading ? <LoadingSpinner /> : null}
						</SypacText>
					</button>
				</SypacButton>
			</div>
		</form>
	);
};

export default CompanyDetails;
